import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import MyWorkHeader from './components/MyWorkHeader';
import AiWorkSummaryCard from './components/summary/AiWorkSummaryCard';
import WeeklyWorkOverview from './components/overview/WeeklyWorkOverview';
import MyTasksWorkspace from './components/tasks/MyTasksWorkspace';
import {
    parseEstimatedStringToMinutes,
    normalizeTaskStatus,
} from './utils/myWorkFormatters';
import {
    getMyWorkOverview,
    getMyWorkAiSummary,
    refreshMyWorkAiSummary,
} from './api/myWorkApi';

dayjs.extend(isBetween);

export default function MyWork({
    allTasks = [],
    loading = false,
    projects = [],
    employees = [],
    openTaskDrawer,
    openCreateDrawer,
    onStatusChange,
    onEdit,
    onDelete,
    onConvertType,
    onTaskCreated,
    onNavigate = null,
}) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const currentAdminId = currentAdmin?._id || currentAdmin?.id;

    // Filters and view state
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
    const [selectedProjectFilter, setSelectedProjectFilter] = useState(null);
    const [selectedSprintFilter, setSelectedSprintFilter] = useState(null);
    const [selectedMilestoneFilter, setSelectedMilestoneFilter] = useState(null);
    const [aiPeriod, setAiPeriod] = useState('This Week');
    const [customRangeDates, setCustomRangeDates] = useState(null);
    const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
    const [backendOverview, setBackendOverview] = useState(null);
    const [aiSummaryData, setAiSummaryData] = useState(null);

    // ── 1. User-Scoped Task Filter ──────────────────────────────────────────
    // Scopes strictly to tasks directly assigned to the logged-in employee (excludes collaborators/reviewers and drafts)
    const userTasks = useMemo(() => {
        if (!allTasks || allTasks.length === 0) return [];

        return allTasks.filter((t) => {
            if (t.isDraft || t.status === 'Draft') return false;
            if (!currentAdminId) return true;
            const assigneeId = t.assignee?._id || t.assignee;
            return assigneeId && String(assigneeId) === String(currentAdminId);
        });
    }, [allTasks, currentAdminId]);

    // ── 2. Calculate Weekly Overview Metrics from User Tasks ────────────────
    const overviewMetrics = useMemo(() => {
        const startOfWeek = dayjs().startOf('week');
        const endOfWeek = dayjs().endOf('week');

        const counts = {
            'Backlog': 0,
            'In Progress': 0,
            'Under Review': 0,
            'On Hold': 0,
            'Completed': 0,
        };

        let totalEstMins = 0;
        let totalEffortMins = 0;
        let totalRemainingMins = 0;

        userTasks.forEach((task) => {
            const norm = normalizeTaskStatus(task.status);

            // Completed rule: only include tasks completed during the current week
            if (norm === 'Completed') {
                const compDate = task.completedAt || task.updatedAt || task.createdAt;
                if (compDate && dayjs(compDate).isBetween(startOfWeek, endOfWeek, null, '[]')) {
                    counts['Completed'] += 1;
                }
            } else if (counts[norm] !== undefined) {
                counts[norm] += 1;
            }

            // Estimate & Effort math for active items
            if (norm !== 'Completed' && norm !== 'Cancelled') {
                const estMins = parseEstimatedStringToMinutes(task.estimatedTime || task.estimate);
                totalEstMins += estMins;

                // Effort from Time Tracking actualTimeMs
                const actualMins = task.actualTimeMs ? Math.round(task.actualTimeMs / 60000) : 0;
                totalEffortMins += actualMins;

                // Remaining work: independent operational value if specified, else est - actual
                if (task.remainingWorkMinutes !== undefined) {
                    totalRemainingMins += task.remainingWorkMinutes;
                } else if (task.remainingWork) {
                    totalRemainingMins += parseEstimatedStringToMinutes(task.remainingWork);
                } else {
                    totalRemainingMins += Math.max(0, estMins - actualMins);
                }
            }
        });

        // Default or schedule-derived capacity (40h = 2400 mins)
        const totalCapacityMins = backendOverview?.totalCapacityMinutes || 2400;
        const remainingCapacityMins = Math.max(0, totalCapacityMins - totalEffortMins);

        return {
            statusCounts: backendOverview?.statusCounts || counts,
            estimatedMinutes: backendOverview?.estimatedMinutes !== undefined ? backendOverview.estimatedMinutes : totalEstMins,
            effortMinutes: backendOverview?.effortMinutes !== undefined ? backendOverview.effortMinutes : totalEffortMins,
            remainingMinutes: backendOverview?.remainingMinutes !== undefined ? backendOverview.remainingMinutes : totalRemainingMins,
            totalCapacityMinutes: totalCapacityMins,
            remainingCapacityMinutes: backendOverview?.remainingCapacityMinutes !== undefined ? backendOverview.remainingCapacityMinutes : remainingCapacityMins,
        };
    }, [userTasks, backendOverview]);

    // ── 3. Helper to format minutes to "Xh Ym" or "0h 0m" ──────────────────
    const formatDuration = (mins = 0) => {
        if (!mins || mins <= 0) return '0h 0m';
        const h = Math.floor(mins / 60);
        const m = Math.round(mins % 60);
        if (h > 0 && m > 0) return `${h}h ${m}m`;
        if (h > 0) return `${h}h`;
        return `${m}m`;
    };

    // ── 4. AI Work Summary Data & Actions ───────────────────────────────────
    const fetchAiSummary = async (periodToFetch, force = false, startDate = null, endDate = null) => {
        setAiSummaryLoading(true);
        try {
            const params = { period: periodToFetch };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const res = force
                ? await refreshMyWorkAiSummary(params)
                : await getMyWorkAiSummary(params);
            if (res?.success && res.data) {
                setAiSummaryData(res.data);
            }
        } catch (err) {
            console.warn('[MyWork] Failed to fetch AI summary:', err);
        } finally {
            setAiSummaryLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;
        const loadOverviewAndAi = async () => {
            const overview = await getMyWorkOverview();
            if (isMounted && overview?.success) {
                setBackendOverview(overview.data);
            }

            setAiSummaryLoading(true);
            try {
                const params = { period: aiPeriod };
                if (customRangeDates) {
                    params.startDate = customRangeDates.startDate;
                    params.endDate = customRangeDates.endDate;
                }
                const aiRes = await getMyWorkAiSummary(params);
                if (isMounted && aiRes?.success && aiRes.data) {
                    setAiSummaryData(aiRes.data);
                }
            } catch (err) {
                console.warn('[MyWork] Failed to fetch AI summary on mount:', err);
            } finally {
                if (isMounted) setAiSummaryLoading(false);
            }
        };

        loadOverviewAndAi();
        return () => {
            isMounted = false;
        };
    }, [currentAdminId, aiPeriod, customRangeDates]);

    const handlePeriodChange = (newPeriod, range = null) => {
        setAiPeriod(newPeriod);
        if (newPeriod === 'Custom Range' && range) {
            setCustomRangeDates(range);
            fetchAiSummary('Custom Range', false, range.startDate, range.endDate);
        } else {
            setCustomRangeDates(null);
            fetchAiSummary(newPeriod, false);
        }
    };

    const handleRefreshAiSummary = () => {
        fetchAiSummary(
            aiPeriod,
            true,
            customRangeDates?.startDate,
            customRangeDates?.endDate
        );
    };

    // ── 5. Truthful Fallback AI Summary from Local Tasks ───────────────────
    const activeTasksCount = userTasks.filter(
        (t) => normalizeTaskStatus(t.status) !== 'Completed' && normalizeTaskStatus(t.status) !== 'Cancelled'
    ).length;

    const uniqueProjects = [...new Set(userTasks.map((t) => t.project?.title || t.project?.name).filter(Boolean))];
    const firstProject = userTasks.find((t) => t.project?.title || t.project?.name)?.project;

    const synthesizedBullets = useMemo(() => {
        if (activeTasksCount === 0) {
            return [
                {
                    type: 'overview',
                    text: 'You currently have no active tasks assigned across any project for this week.',
                    pillText: '0 active tasks',
                    pillColor: 'blue',
                },
                {
                    type: 'status',
                    text: 'All queues are clear. When tasks are assigned to you, your work intelligence briefing will update here automatically.',
                    pillText: 'Queue Clear',
                    pillColor: 'blue',
                },
            ];
        }

        const b = [];
        b.push({
            type: 'overview',
            text: `You currently have ${activeTasksCount} active task${activeTasksCount > 1 ? 's' : ''} across ${uniqueProjects.length} project${uniqueProjects.length > 1 ? 's' : ''}.`,
            projectName: firstProject?.title || firstProject?.name,
            projectId: firstProject?._id,
            pillText: `${activeTasksCount} active tasks`,
            pillColor: 'blue',
        });

        const inProg = overviewMetrics.statusCounts['In Progress'] || 0;
        const inReview = overviewMetrics.statusCounts['Under Review'] || 0;
        if (inProg > 0 || inReview > 0) {
            b.push({
                type: 'status',
                text: `${inProg} task${inProg === 1 ? '' : 's'} in progress, while ${inReview} task${inReview === 1 ? '' : 's'} are currently under review.`,
                pillText: `${inProg} In Progress`,
                pillColor: 'blue',
            });
        }

        const dueToday = userTasks.find((t) => dayjs(t.dueDate).isSame(dayjs(), 'day'));
        if (dueToday) {
            b.push({
                type: 'due',
                taskId: dueToday._id,
                taskName: dueToday.title,
                text: 'is due today.',
                pillText: 'Due Today',
                pillColor: 'blue',
            });
        }

        const variance = userTasks.find((t) => {
            const est = parseEstimatedStringToMinutes(t.estimatedTime);
            const act = t.actualTimeMs ? t.actualTimeMs / 60000 : 0;
            return act > est && est > 0;
        });
        if (variance) {
            const est = parseEstimatedStringToMinutes(variance.estimatedTime);
            const act = Math.round(variance.actualTimeMs / 60000);
            b.push({
                type: 'variance',
                taskId: variance._id,
                taskName: variance.title,
                text: `has recorded ${formatDuration(act)} against a ${formatDuration(est)} estimate.`,
                pillText: `+${formatDuration(act - est)}`,
                pillColor: 'amber',
            });
        }

        return b;
    }, [activeTasksCount, uniqueProjects.length, firstProject, overviewMetrics.statusCounts, userTasks]);

    const computedAiSummary = aiSummaryData || {
        activeTaskCount: activeTasksCount,
        projectCount: uniqueProjects.length,
        inProgressCount: overviewMetrics.statusCounts['In Progress'] || 0,
        underReviewCount: overviewMetrics.statusCounts['Under Review'] || 0,
        backlogCount: overviewMetrics.statusCounts['Backlog'] || 0,
        effortDuration: formatDuration(overviewMetrics.effortMinutes),
        estimatedDuration: formatDuration(overviewMetrics.estimatedMinutes),
        remainingDuration: formatDuration(overviewMetrics.remainingMinutes),
        generatedAt: new Date().toISOString(),
        updatedTimeAgo: 'Just now',
        dueTodayTask: userTasks.find((t) => dayjs(t.dueDate).isSame(dayjs(), 'day')) || null,
        varianceTask: userTasks.find((t) => {
            const est = parseEstimatedStringToMinutes(t.estimatedTime);
            const act = t.actualTimeMs ? t.actualTimeMs / 60000 : 0;
            return act > est && est > 0;
        }) || null,
        bullets: synthesizedBullets,
        detailedBullets: synthesizedBullets,
    };

    const handleNewTask = (initialStatus = 'Backlog', itemType = 'task') => {
        if (openCreateDrawer) {
            openCreateDrawer(initialStatus || 'Backlog', itemType || 'task');
        } else if (openTaskDrawer) {
            openTaskDrawer(null, initialStatus || 'Backlog');
        }
    };

    return (
        <section className="space-y-6 max-w-[1600px] mx-auto pb-12">
            {/* 1. Top Header */}
            <MyWorkHeader onNavigate={onNavigate} />

            {/* 2. AI Work Summary Section */}
            <div data-tour="mywork-ai-briefing">
                <AiWorkSummaryCard
                    summaryData={computedAiSummary}
                    loading={aiSummaryLoading}
                    period={aiPeriod}
                    onPeriodChange={handlePeriodChange}
                    onRefresh={handleRefreshAiSummary}
                    onGenerate={handleRefreshAiSummary}
                    onTaskClick={(taskId) => openTaskDrawer?.(taskId)}
                    onProjectClick={(projId) => setSelectedProjectFilter(projId)}
                    onSprintClick={(sprintName) => setSelectedSprintFilter(sprintName)}
                    onMilestoneClick={(milestoneId) => setSelectedMilestoneFilter(milestoneId)}
                />
            </div>

            {/* 3. Weekly Work Overview Section */}
            <div data-tour="mywork-capacity-overview">
                <WeeklyWorkOverview
                    statusCounts={overviewMetrics.statusCounts}
                    selectedStatus={selectedStatusFilter}
                    onStatusSelect={(statusKey) => setSelectedStatusFilter(statusKey)}
                    estimatedMinutes={overviewMetrics.estimatedMinutes}
                    effortMinutes={overviewMetrics.effortMinutes}
                    remainingMinutes={overviewMetrics.remainingMinutes}
                    totalCapacityMinutes={overviewMetrics.totalCapacityMinutes}
                    remainingCapacityMinutes={overviewMetrics.remainingCapacityMinutes}
                />
            </div>

            {/* 4. My Tasks Workspace Section */}
            <div data-tour="mywork-tasks-workspace">
                <MyTasksWorkspace
                    tasks={userTasks}
                    loading={loading}
                    selectedStatus={selectedStatusFilter}
                    onStatusSelect={(status) => setSelectedStatusFilter(status)}
                    onClearStatusFilter={() => {
                        setSelectedStatusFilter(null);
                        setSelectedProjectFilter(null);
                        setSelectedSprintFilter(null);
                        setSelectedMilestoneFilter(null);
                    }}
                    selectedProject={selectedProjectFilter}
                    onProjectSelect={(projId) => setSelectedProjectFilter(projId)}
                    selectedSprint={selectedSprintFilter}
                    onSprintSelect={(sprint) => setSelectedSprintFilter(sprint)}
                    selectedMilestone={selectedMilestoneFilter}
                    onMilestoneSelect={(milestone) => setSelectedMilestoneFilter(milestone)}
                    onTaskClick={(taskId) => openTaskDrawer?.(taskId)}
                    onStatusChange={onStatusChange}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onConvertType={onConvertType}
                    onNewTask={handleNewTask}
                />
            </div>
        </section>
    );
}
